import { validateEnumTranslations } from '../../safety_checks';

/**
 * Import de base clients CSV/XLSX — enums partagés (dual-defined avec Prisma
 * pour `ProCustomerImportStatus`, cf. règle CLAUDE.md sur les enums dual-defined).
 */

export enum ProCustomerImportStatus {
  PENDING_MAPPING = 'PENDING_MAPPING',
  READY = 'READY',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  COMPLETED_WITH_ERRORS = 'COMPLETED_WITH_ERRORS',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

/**
 * Champ cible d'une colonne du fichier importé. Les valeurs `TAG` et `IGNORE`
 * sont des pseudo-champs de mapping (colonne → tags libres / colonne ignorée).
 */
export enum ImportTargetField {
  FIRSTNAME = 'firstname',
  LASTNAME = 'lastname',
  EMAIL = 'email',
  PHONE_NUMBER = 'phone_number',
  BIRTHDAY = 'birthday',
  CUSTOMER_SINCE = 'customer_since',
  ADDRESS = 'address',
  COMMENT = 'comment',
  GENDER = 'gender',
  SEND_MARKETING_EMAIL = 'send_marketing_email',
  SEND_MARKETING_SMS = 'send_marketing_sms',
  TAG = 'tag',
  IGNORE = 'ignore',
}

export enum ImportDuplicateStrategy {
  SKIP = 'skip',
  UPDATE = 'update',
}

export enum ImportMatchKeys {
  EMAIL = 'email',
  PHONE = 'phone',
  EMAIL_AND_PHONE = 'email_and_phone',
}

export enum ImportRowAction {
  CREATE = 'create',
  UPDATE = 'update',
  SKIP = 'skip',
  ERROR = 'error',
}

/**
 * Clés d'erreur / warning par ligne (dry-run + rapport final). Traduites côté
 * front via ce namespace ; le rapport CSV embarque le libellé traduit.
 */
export enum ImportRowErrorKey {
  NO_IDENTIFIER = 'NO_IDENTIFIER',
  NO_MATCH_KEY = 'NO_MATCH_KEY',
  INVALID_EMAIL = 'INVALID_EMAIL',
  INVALID_PHONE = 'INVALID_PHONE',
  INVALID_BIRTHDAY = 'INVALID_BIRTHDAY',
  INVALID_CUSTOMER_SINCE = 'INVALID_CUSTOMER_SINCE',
  CUSTOMER_SINCE_IN_FUTURE = 'CUSTOMER_SINCE_IN_FUTURE',
  DUPLICATE_IN_FILE = 'DUPLICATE_IN_FILE',
  DUPLICATE_IN_BASE = 'DUPLICATE_IN_BASE',
  BLACKLISTED = 'BLACKLISTED',
  CONSENT_NOT_REENABLED = 'CONSENT_NOT_REENABLED',
  TAG_NOT_CREATED = 'TAG_NOT_CREATED',
  INVALID_CUSTOM_FIELD_VALUE = 'INVALID_CUSTOM_FIELD_VALUE',
}

export const TRANSLATE_KEY_PRO_CUSTOMER_IMPORT_STATUS = 'PRO_CUSTOMER_IMPORT_STATUS_';
export const TRANSLATE_KEY_IMPORT_TARGET_FIELD = 'IMPORT_TARGET_FIELD_';
export const TRANSLATE_KEY_IMPORT_ROW_ACTION = 'IMPORT_ROW_ACTION_';
export const TRANSLATE_KEY_IMPORT_ROW_ERROR = 'IMPORT_ROW_ERROR_';

export const ProCustomerImportStatusTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_PRO_CUSTOMER_IMPORT_STATUS}${ProCustomerImportStatus.PENDING_MAPPING}`]: 'Mapping à confirmer',
    [`${TRANSLATE_KEY_PRO_CUSTOMER_IMPORT_STATUS}${ProCustomerImportStatus.READY}`]: 'Prêt à lancer',
    [`${TRANSLATE_KEY_PRO_CUSTOMER_IMPORT_STATUS}${ProCustomerImportStatus.RUNNING}`]: 'Import en cours',
    [`${TRANSLATE_KEY_PRO_CUSTOMER_IMPORT_STATUS}${ProCustomerImportStatus.COMPLETED}`]: 'Terminé',
    [`${TRANSLATE_KEY_PRO_CUSTOMER_IMPORT_STATUS}${ProCustomerImportStatus.COMPLETED_WITH_ERRORS}`]:
      'Terminé avec erreurs',
    [`${TRANSLATE_KEY_PRO_CUSTOMER_IMPORT_STATUS}${ProCustomerImportStatus.FAILED}`]: 'Échec',
    [`${TRANSLATE_KEY_PRO_CUSTOMER_IMPORT_STATUS}${ProCustomerImportStatus.CANCELLED}`]: 'Annulé',
  },
};

export const ImportTargetFieldTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_IMPORT_TARGET_FIELD}${ImportTargetField.FIRSTNAME}`]: 'Prénom',
    [`${TRANSLATE_KEY_IMPORT_TARGET_FIELD}${ImportTargetField.LASTNAME}`]: 'Nom',
    [`${TRANSLATE_KEY_IMPORT_TARGET_FIELD}${ImportTargetField.EMAIL}`]: 'Email',
    [`${TRANSLATE_KEY_IMPORT_TARGET_FIELD}${ImportTargetField.PHONE_NUMBER}`]: 'Téléphone',
    [`${TRANSLATE_KEY_IMPORT_TARGET_FIELD}${ImportTargetField.BIRTHDAY}`]: 'Date de naissance',
    [`${TRANSLATE_KEY_IMPORT_TARGET_FIELD}${ImportTargetField.CUSTOMER_SINCE}`]: 'Client depuis',
    [`${TRANSLATE_KEY_IMPORT_TARGET_FIELD}${ImportTargetField.ADDRESS}`]: 'Adresse',
    [`${TRANSLATE_KEY_IMPORT_TARGET_FIELD}${ImportTargetField.COMMENT}`]: 'Commentaire',
    [`${TRANSLATE_KEY_IMPORT_TARGET_FIELD}${ImportTargetField.GENDER}`]: 'Civilité',
    [`${TRANSLATE_KEY_IMPORT_TARGET_FIELD}${ImportTargetField.SEND_MARKETING_EMAIL}`]: 'Consentement email',
    [`${TRANSLATE_KEY_IMPORT_TARGET_FIELD}${ImportTargetField.SEND_MARKETING_SMS}`]: 'Consentement SMS',
    [`${TRANSLATE_KEY_IMPORT_TARGET_FIELD}${ImportTargetField.TAG}`]: 'Tag',
    [`${TRANSLATE_KEY_IMPORT_TARGET_FIELD}${ImportTargetField.IGNORE}`]: 'Ignorer',
  },
};

export const ImportRowActionTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_IMPORT_ROW_ACTION}${ImportRowAction.CREATE}`]: 'Création',
    [`${TRANSLATE_KEY_IMPORT_ROW_ACTION}${ImportRowAction.UPDATE}`]: 'Mise à jour',
    [`${TRANSLATE_KEY_IMPORT_ROW_ACTION}${ImportRowAction.SKIP}`]: 'Ignorée',
    [`${TRANSLATE_KEY_IMPORT_ROW_ACTION}${ImportRowAction.ERROR}`]: 'Erreur',
  },
};

export const ImportRowErrorTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_IMPORT_ROW_ERROR}${ImportRowErrorKey.NO_IDENTIFIER}`]:
      'Ligne sans email, téléphone ni nom + prénom : non importée',
    [`${TRANSLATE_KEY_IMPORT_ROW_ERROR}${ImportRowErrorKey.NO_MATCH_KEY}`]:
      'Ligne sans email ni téléphone : sera créée en doublon si vous ré-importez ce fichier',
    [`${TRANSLATE_KEY_IMPORT_ROW_ERROR}${ImportRowErrorKey.INVALID_EMAIL}`]: 'Email invalide : champ non importé',
    [`${TRANSLATE_KEY_IMPORT_ROW_ERROR}${ImportRowErrorKey.INVALID_PHONE}`]: 'Téléphone invalide : champ non importé',
    [`${TRANSLATE_KEY_IMPORT_ROW_ERROR}${ImportRowErrorKey.INVALID_BIRTHDAY}`]:
      'Date de naissance invalide : champ non importé',
    [`${TRANSLATE_KEY_IMPORT_ROW_ERROR}${ImportRowErrorKey.INVALID_CUSTOMER_SINCE}`]:
      'Date « client depuis » invalide (jour, mois et année attendus) : champ non importé',
    [`${TRANSLATE_KEY_IMPORT_ROW_ERROR}${ImportRowErrorKey.CUSTOMER_SINCE_IN_FUTURE}`]:
      'Date « client depuis » dans le futur : champ non importé',
    [`${TRANSLATE_KEY_IMPORT_ROW_ERROR}${ImportRowErrorKey.DUPLICATE_IN_FILE}`]:
      'Doublon dans le fichier : ligne ignorée',
    [`${TRANSLATE_KEY_IMPORT_ROW_ERROR}${ImportRowErrorKey.DUPLICATE_IN_BASE}`]:
      'Client déjà présent dans votre base : ligne ignorée',
    [`${TRANSLATE_KEY_IMPORT_ROW_ERROR}${ImportRowErrorKey.BLACKLISTED}`]:
      'Email ou téléphone désinscrit (liste noire) : ligne ignorée',
    [`${TRANSLATE_KEY_IMPORT_ROW_ERROR}${ImportRowErrorKey.CONSENT_NOT_REENABLED}`]:
      'Consentement non réactivé : ce client s’était désabonné',
    [`${TRANSLATE_KEY_IMPORT_ROW_ERROR}${ImportRowErrorKey.TAG_NOT_CREATED}`]:
      'Tag inconnu non créé (création de tags désactivée)',
    [`${TRANSLATE_KEY_IMPORT_ROW_ERROR}${ImportRowErrorKey.INVALID_CUSTOM_FIELD_VALUE}`]:
      'Valeur refusée par le champ personnalisé (type, option ou bornes) : champ non renseigné',
  },
};

/**
 * En-têtes du rapport d'erreurs CSV généré à la fin du job d'import (contenu
 * user-visible → passe par le système de traduction, fr-FR only).
 */
export const IMPORT_REPORT_HEADERS_TRANSLATION = {
  'fr-FR': {
    row: 'Ligne',
    action: 'Action',
    field: 'Champ',
    reason: 'Motif',
    value: 'Valeur source',
  },
};

//DONT TOUCH below this line
validateEnumTranslations(
  ProCustomerImportStatus,
  ProCustomerImportStatusTranslation,
  TRANSLATE_KEY_PRO_CUSTOMER_IMPORT_STATUS,
  true,
);
validateEnumTranslations(ImportTargetField, ImportTargetFieldTranslation, TRANSLATE_KEY_IMPORT_TARGET_FIELD, true);
validateEnumTranslations(ImportRowAction, ImportRowActionTranslation, TRANSLATE_KEY_IMPORT_ROW_ACTION, true);
validateEnumTranslations(ImportRowErrorKey, ImportRowErrorTranslation, TRANSLATE_KEY_IMPORT_ROW_ERROR, true);
